import { useRouter } from "@shared/client/navigation";
import { useState } from "react";
import { Plus } from "lucide-react";
import { OfferFavicon } from "@/components/offer-favicon";
import type { OfferSummary } from "@shared/lib/offers-shared";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { useT } from "@shared/client/locale";

/**
 * L'offre consultée, dans la topbar.
 *
 * Un compte peut surveiller plusieurs offres — deux produits, deux marchés —
 * et tout l'espace en dépend : le pipeline, les passages, le cycle, la
 * mémoire de l'agent. Le choix se fait donc une fois, en haut, et vaut pour
 * tous les écrans ; il n'est pas répété dans chaque formulaire.
 *
 * Le sélecteur ne s'affiche pas tant qu'il n'y a rien à choisir : un compte
 * avec une seule offre n'a pas besoin qu'on lui rappelle laquelle.
 */
const ADD_OFFER = "__add__";

export function OfferSwitcher({ offers, activeOfferId }: {
  offers: OfferSummary[];
  activeOfferId: string | null;
}) {
  const router = useRouter();
  const t = useT();
  const [pending, setPending] = useState(false);

  if (offers.length < 2) return null;

  async function select(value: string) {
    if (value === ADD_OFFER) {
      router.push("/app/offer");
      return;
    }
    setPending(true);
    try {
      await fetch("/api/offers/select", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ offerId: value }),
      });
      router.refresh();
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="mr-auto flex min-w-0 items-center gap-3">
      <Select disabled={pending} onValueChange={select} value={activeOfferId ?? undefined}>
        <SelectTrigger
          aria-label={t.shell.offerSwitcher.label}
          className="h-[38px] max-w-[280px] gap-2 text-xs font-semibold max-md:max-w-[200px]"
        >
          <SelectValue placeholder={t.shell.offerSwitcher.placeholder} />
        </SelectTrigger>
        <SelectContent>
          {offers.map((offer) => (
            <SelectItem key={offer.id} value={offer.id}>
              {/* Radix rend le libellé dans un conteneur en ligne : sans cette
                  rangée flex, l'icône passe au-dessus du texte. */}
              <span className="flex min-w-0 items-center gap-2">
                <OfferFavicon size={16} url={offer.url} />
                <span className="truncate">{offer.name}</span>
              </span>
            </SelectItem>
          ))}
          <SelectSeparator />
          <SelectItem value={ADD_OFFER}>
            <span className="flex items-center gap-2">
              <Plus aria-hidden="true" className="size-4 text-muted-foreground" /> {t.shell.offerSwitcher.add}
            </span>
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
